// Publishes ONE complete add_to_cart per cart add, with every line and the real
// discounted prices. Replaces the Stape widget as the Meta event source: that
// widget keeps only the last line of a multi-item add and reads list price, so a
// two-line bundle reached Meta as one line at full price.
//
// The payload mirrors add_to_cart_stape exactly, so every existing GTM variable
// (dlv - ecommerce.value/currency/items, meta - contents, meta - content_ids)
// keeps working untouched. Only the tag triggers move to add_to_cart_aggregated.
//
// Hooks the theme pub/sub rather than the network, so it covers every add path
// the theme owns: bundle offer, product form, quick add, drawer upsell. Sends
// only what the add changed, also when the product was already in the cart.
(function () {
  'use strict';

  function clientId() {
    var m = document.cookie.match(/(?:^|;\s*)_shopify_y=([^;]+)/);
    return m ? m[1] : null;
  }

  // Reuse whatever pagetype Stape last reported, so GTM variables stay
  // consistent between our event and theirs.
  function pageType() {
    var dl = window.dataLayer || [];
    for (var i = dl.length - 1; i >= 0; i--) {
      if (dl[i] && dl[i].ecomm_pagetype) return dl[i].ecomm_pagetype;
    }
    return 'product';
  }

  // /cart/add.js returns {items:[...]} for a multi-item add and the bare line
  // object for a single-item add. Normalise both to an array.
  function linesFrom(cartData) {
    if (!cartData) return [];
    if (Array.isArray(cartData.items)) return cartData.items;
    if (cartData.id) return [cartData];
    return [];
  }

  // `price` and `line_price` in the cart JSON are LIST prices. Only `final_*`
  // is net of the automatic discount. Reading the wrong one is exactly the
  // Stape widget bug.
  //
  // ponytail: keep this object small. The Stape Data Tag picks its transport by
  // payload size (`JSON.stringify(userAndCustomData).length > 1500 ? post : get`).
  // The POST branch must first load https://stapecdn.com/dtag/v8.js, which ad
  // blockers block: injectScript never calls back, gtmOnFailure runs, the tag
  // reports Failed and nothing reaches the server. Verified at runtime on
  // 2026-07-26 (transferSize 0, gtm_dataTagScriptLoadedCache unset). So item_url
  // and imageURL are deliberately omitted: no Meta mapping reads them
  // (`meta - contents` uses only item_id, price, quantity, item_name) and they
  // were the two heaviest fields, worth ~425 chars on a two-line bundle.
  // Ceiling: a bundle of 4+ lines crosses 1500 anyway. Fixing that for real
  // needs Stape to serve dtag from the sst.zetafresh.com custom domain.
  //
  // `total` is what the add cost on this line, in cents. The unit price is
  // derived from it rather than read from final_price: a tiered discount can
  // reprice the units already in the cart, and price x quantity must still
  // add up to the event value.
  function mapLine(line, quantity, total) {
    return {
      item_id: String(line.product_id),
      item_sku: line.sku || null,
      item_variant: String(line.id),
      item_name: line.product_title || line.title,
      variant_name: line.variant_title,
      item_category: line.product_type || null,
      item_brand: line.vendor || null,
      price: Math.round(total / quantity) / 100,
      quantity: quantity,
    };
  }

  function lineTotal(line) {
    return line.final_line_price != null ? line.final_line_price : line.line_price;
  }

  // /cart/add.js returns every touched line with its new TOTAL quantity and
  // price, so adding a product already in the cart would report the whole line
  // again (seen on 2026-10-15: 276.59 sent for a 149.98 add). Keep the cart as
  // it was before the add and send only the difference.
  //
  // Keyed by variant, not by line key: Shopify changes the key of a discounted
  // line when its quantity changes (verified 2026-10-15). The theme adds no
  // line item properties, so a variant sits on a single line.
  var before = {};

  function variantOf(line) {
    return String(line.variant_id || line.id);
  }

  function remember(cart) {
    before = {};
    (cart.items || []).forEach(function (line) {
      var prev = before[variantOf(line)] || { quantity: 0, total: 0 };
      before[variantOf(line)] = { quantity: prev.quantity + line.quantity, total: prev.total + lineTotal(line) };
    });
  }

  // A cart exists only once the cart cookie is set; without it the cart is
  // empty and a page view costs no request.
  var loaded = !/(?:^|;\s*)cart=/.test(document.cookie);
  var ready = loaded
    ? Promise.resolve()
    : fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) { return response.json(); })
        .then(remember)
        .catch(function () {})
        .then(function () { loaded = true; });

  // What each line gained in this add. An add always raises the quantity, so
  // a line that did not grow means the snapshot is stale (cart changed in
  // another tab): count the whole line rather than drop the add.
  function addedLines(lines) {
    return lines.map(function (line) {
      var prev = before[variantOf(line)];
      var grown = prev && line.quantity > prev.quantity;
      before[variantOf(line)] = { quantity: line.quantity, total: lineTotal(line) };
      return {
        line: line,
        quantity: grown ? line.quantity - prev.quantity : line.quantity,
        total: grown ? Math.max(0, lineTotal(line) - prev.total) : lineTotal(line),
      };
    });
  }

  subscribe('cart-update', function (payload) {
    if (!payload) return;
    // 'cart-items' is a quantity edit or removal inside the drawer: not an
    // add, but its cartData is the full cart, so it refreshes the snapshot.
    if (payload.source !== 'product-form') {
      if (payload.cartData && Array.isArray(payload.cartData.items)) remember(payload.cartData);
      return;
    }

    var lines = linesFrom(payload.cartData);
    if (!lines.length) return;
    // Compute right away, so a later cart-update cannot move the snapshot
    // first. Only an add during the first page load waits for /cart.js.
    if (loaded) push(addedLines(lines));
    else ready.then(function () { push(addedLines(lines)); });
  });

  function push(added) {
    var value = added.reduce(function (sum, a) { return sum + a.total; }, 0) / 100;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'add_to_cart_aggregated',
      user_data: { shopify_client_id: clientId() },
      ecommerce: {
        // A number, not a string: GA4 reads a string value as a text
        // parameter (ep.value) instead of the event value (epn.value).
        value: Number(value.toFixed(2)),
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR',
        items: added.map(function (a) { return mapLine(a.line, a.quantity, a.total); }),
      },
      actual_url: window.location.href,
      ecomm_pagetype: pageType(),
    });
  }
})();
